import React, { useState, useRef, useLayoutEffect } from 'react';

import { useSpring, animated } from '@react-spring/web';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import styled from 'styled-components';

import { useBuilderConstructorCategoryNav } from '~/builder/utils/useBuilderConstructorCategoryNav';
import { slugify } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import useBuilderConstructorGridInputs from '../../hooks/useBuilderConstructorGridInputs';

import MobileGridCategoryTree from './MobileGridCategoryTree';

const DynamicFormattedMessage = FormattedMessage;

const CategoryDropdownContainer = styled.div`
  position: relative;
  z-index: 15;
`;

const CategoryDropdownButton = styled.button`
  width: 100%;
  font-weight: bold;
  font-size: 20px;
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gridCategoryTreeTextColor};
  padding: 19px 8px;
  border: none;
  background: ${({ theme }) => theme.colors.palette.white};
  border-bottom: ${({ $isExpanded }) => ($isExpanded ? '1px solid' : 'none')};
  position: relative;
  cursor: pointer;
  font-family: inherit;
  z-index: 150;
  letter-spacing: -0.5px;
  text-transform: capitalize;
`;

const Overlay = styled(animated.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 100;
`;

const Panel = styled(animated.div)`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transform-origin: top center;
  z-index: 150;
  will-change: height;
`;

const IconWrapper = styled.span`
  padding-left: 4px;
  padding-top: 4px;
  display: flex;
`;

const MobileGridCategoryDropdown = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { sideNav, builderModel } = useBuilderConstructorGridInputs();
  const showStickySideNavHeader = sideNav?.shouldShowCategoryNav;
  const clientCategoryNavSettings = useBuilderConstructorCategoryNav({
    sideNav,
    builderModel,
  });
  // can't destructure this because it will be undefined if the category nav is not enabled
  const activeCategory = clientCategoryNavSettings?.activeCategory;

  // measure content for accordion height
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  useLayoutEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [activeCategory]);

  // spring for overlay opacity only
  const overlaySpring = useSpring({
    opacity: isExpanded ? 1 : 0,
    config: { tension: 250, friction: 30 },
  });

  // spring for panel height only
  const panelSpring = useSpring({
    height: isExpanded ? contentHeight : 0,
    config: { tension: 250, friction: 30 },
  });

  if (!showStickySideNavHeader || !activeCategory?.label) {
    return null;
  }

  return (
    <CategoryDropdownContainer>
      <CategoryDropdownButton
        data-expanded={isExpanded}
        onClick={() => setIsExpanded((v) => !v)}
      >
        <DynamicFormattedMessage
          id={`site_product_category.${slugify(activeCategory?.label)}`}
          defaultMessage={activeCategory?.label}
        />
        <IconWrapper>
          {isExpanded ? <IoIosArrowUp /> : <IoIosArrowDown />}
        </IconWrapper>
      </CategoryDropdownButton>

      {isExpanded && (
        <Overlay style={overlaySpring} onClick={() => setIsExpanded(false)} />
      )}

      <Panel style={panelSpring}>
        <div ref={contentRef}>
          <MobileGridCategoryTree activeCategory={activeCategory} />
        </div>
      </Panel>
    </CategoryDropdownContainer>
  );
};

export default MobileGridCategoryDropdown;
